import React, { useEffect, useState, useRef } from "react";
import CalendarPlus from "../assets/icon/svgs/CalendarPlus.svg";
import CalendarChek from "../assets/icon/svgs/CalendarChek.svg";
import CrossRight from "../assets/icon/svgs/CrossRight.svg?react";
import TabBar from "@/components/tabBar/TabBar";
import { Loader } from "@/assets/icon/loader";
import { useTranslation } from "react-i18next";
import BillsService, {
  EmergencyInfo,
  IBuilding,
  IHouseManagerBillsPage,
  IMetersPage,
  INotices,
} from "@/api-client/api/services/billsService/billsService";
import HeaderCommon from "@/components/header/HeaderCommon";
import { getSettings } from "@/services/settings";
import SelectTab from "@/components/selectTab/SelectTab";
import { OpenPeriod } from "@/components/pop-ups/OpenPeriod";
import {
  HouseManagerApartamentInfo,
  HouseManagerBills,
  HouseManagerHousesInfo,
  HouseManagerNoticesAndNews,
  InvoiceEntity,
} from "@/components/HouseManagerComponents/HouseManagerComponents";
import { HouseManagerMeters } from "@/components/HouseManagerComponents/HouseManagerComponents";
import Pagination from "@/components/customComponents/Pagination";
import { DashboardTabs } from "@/components/customComponents/DashboardTabs";
import Edit from "../assets/icon/svgs/Edit.svg?react";
import Plus from "../assets/icon/svgs/Plus.svg?react";
import { ConfirmClosePeriod } from "@/components/pop-ups/ConfirmClosePeriod";
import HouseManagerApi, {
  ApartmentInfoResult,
  BillingInvoiceUpdate,
} from "@/api-client/api/services/houseManager/houseManager";
import { ConfirmCancelPeriod } from "@/components/pop-ups/ConfirmCancelPeriod";
import { PopUpCreateNews } from "@/components/pop-ups/PopUpCreateNews";
import { CalendarForm } from "@/components/ui/calendarPicker";

// import HouseManager, {
//   BillingInvoiceUpdate,
// } from "@/api-client/api/services/houseManager/houseManager";
// import HouseManager from "@/api-client/api/services/houseManager/houseManager";
interface FilterSelector {
  label: string;
  value: string;
  options: { label: string; value: string }[];
  translate?: string;
}

const HouseManager: React.FC = () => {
  const { t } = useTranslation();
  const refetchIntervalBillsUpdateRef = useRef<NodeJS.Timeout | null>(null);
  const refSendHouseInfo = useRef<() => Promise<boolean>>(async () => false);

  const [bills, setBills] = useState<IHouseManagerBillsPage>();
  const [meters, setMeters] = useState<IMetersPage>();
  const [notices, setNotices] = useState<INotices[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingBills, setIsLoadingBills] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string>
  >({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(10);
  const [isEditingCurrentPeriod, setIsEditingCurrentPeriod] = useState(false);
  const [isEditingClosedPeriod, setIsEditingClosedPeriod] = useState(false);
  const [isEditingHousesInfo, setIsEditingHousesInfo] = useState(false);
  const [emergencyInfo, setEmergencyInfo] = useState<EmergencyInfo>();
  const [periodMonthYear, setPeriodMonthYear] = useState<string | undefined>();
  const [payBeforeDateForCurrentPeriod, setPayBeforeDateForCurrentPeriod] =
    useState<string | undefined>();
  const [usersInfo, setUsersInfo] = useState<ApartmentInfoResult[]>();

  const [changedEntitiesBills, setChangedEntitiesBills] = useState<
    InvoiceEntity[]
  >([]);
  const [localPdfFiles, setLocalPdfFiles] = useState<Record<string, File>>({});

  const companyOptions = [
    { label: t("house_manager.bills"), value: "bills" },
    { label: t("house_manager.meter"), value: "meters" },
    { label: t("house_manager.notices_and_news"), value: "notices_and_news" },
    { label: t("house_manager.apartment_info"), value: "apartment_info" },
    { label: t("house_manager.houses_info"), value: "houses_info" },
  ];
  const periodOptions = [
    { name: t("house_manager.current_period"), value: "current_period" },
    { name: t("house_manager.closed_periods"), value: "closed_periods" },
  ];

  const [selectedPeriodOptions, setSelectedPeriodOptions] = useState(
    periodOptions[1] as { name: string; value: string }
  );
  const [selectedCompany, setSelectedCompany] = useState(companyOptions[0]);
  const [buildings, setBuildings] = useState<IBuilding>();
  const [buildAddress, setBuildAddress] = useState<
    {
      label: string;
      value: string;
    }[]
  >([]);
  const [selectedBuildAddress, setSelectedBuildAddress] = useState<{
    label: string;
    value: string;
  }>({ label: "", value: "" });
  const [apartmentNumber, setApartmentNumber] = useState<
    {
      label: string;
      value?: number;
    }[]
  >([]);

  const editCurrentPeriod = () => {
    setIsEditingCurrentPeriod(!isEditingCurrentPeriod);
    postInvoiceDueDate();
  };

  const postInvoiceDueDate = async () => {
    if (
      selectedPeriodOptions.value !== "current_period" ||
      !isEditingCurrentPeriod ||
      !payBeforeDateForCurrentPeriod
    )
      return;
    await HouseManagerApi.postInvoiceDueDate(
      selectedBuildAddress.value,
      payBeforeDateForCurrentPeriod
    );
  };

  const handleFieldChangeRefetch = async ({
    setLoader = true,
  }: {
    setLoader?: boolean;
  }) => {
    if (changedEntitiesBills.length > 0) {
      try {
        if (setLoader) setIsLoadingBills(true);
        await HouseManagerApi.billingInvoices(
          changedEntitiesBills as BillingInvoiceUpdate[]
        )
          .then(() => {
            setChangedEntitiesBills([]);
            fetchBills(
              selectedPeriodOptions.value === "closed_periods" ? false : true
            );
            if (setLoader) setIsLoadingBills(false);
          })
          .catch((err) => {
            console.error(err);
          });
      } catch (err) {
        console.error(err);
      }
    }
  };

  const handleSave = async () => {
    const result = await refSendHouseInfo.current?.();
    return result;
  };

  const fetchNotices = async () => {
    if (selectedCompany.value !== "notices_and_news") return;
    try {
      setIsLoading(true);
      const notices = await BillsService.getNotices(
        Number(selectedBuildAddress.value)
      );
      setNotices(notices);
    } catch (error) {
      console.error("Error fetching notices:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    handleFieldChangeRefetch({ setLoader: true });
  }, [
    isEditingCurrentPeriod,
    isEditingClosedPeriod,
    selectedPeriodOptions,
    selectedCompany,
  ]);

  useEffect(() => {
    refetchIntervalBillsUpdateRef.current = setInterval(
      () => handleFieldChangeRefetch({ setLoader: false }),
      10000
    );
    return () => {
      if (refetchIntervalBillsUpdateRef.current) {
        clearInterval(refetchIntervalBillsUpdateRef.current);
      }
    };
  }, [changedEntitiesBills, page]);

  // HouseManagerApi.billingInvoices END

  const fetchBuildings = async () => {
    try {
      const buildings = await BillsService.getBuildings();
      setBuildings(buildings);

      const formattedAddresses = buildings?.buildings?.map((building) => ({
        label: building.address,
        value: building.building_id.toString(),
      }));

      setBuildAddress(formattedAddresses ?? []);
      setSelectedBuildAddress(
        formattedAddresses?.[0] ?? { label: "", value: "" }
      );
    } catch (error) {
      console.error("Error fetching buildings:", error);
    }
  };

  const fetchBills = async (
    periodClosed: boolean,
    dontSetData: boolean = false
  ) => {
    if (selectedCompany.value !== "bills" || !selectedBuildAddress.value)
      return;
    try {
      setIsLoading(true);
      const bills = await BillsService.getBills<IHouseManagerBillsPage>(
        undefined,
        undefined,
        selectedPeriodOptions.value === "closed_periods"
          ? selectedFilters.month
          : undefined,
        selectedPeriodOptions.value === "closed_periods"
          ? selectedFilters.year
          : undefined,
        page,
        pageSize,
        selectedPeriodOptions.value === "closed_periods"
          ? (selectedFilters.status as "paid" | "unpaid" | "debt" | undefined)
          : undefined,
        selectedBuildAddress.value,
        selectedFilters.apartment,
        periodClosed,
        "all"
      );
      if (!dontSetData) {
        setBills(bills);
      }
      if (periodClosed) {
        setPeriodMonthYear(bills.apartments[0].invoices[0].billing_month);
      }
      setTotalPages(bills.total_pages);
    } catch (error) {
      console.error("Error fetching bills:", error);
    } finally {
      setIsLoading(false);
    }
    return [];
  };

  const fetchMeters = async () => {
    if (selectedCompany.value !== "meters") return;
    setIsLoading(true);
    try {
      const meters = await BillsService.getMeters(
        selectedBuildAddress.value,
        selectedFilters.type_of_meter,
        selectedFilters.apartment,
        page,
        pageSize
      );
      setMeters(meters);
      setTotalPages(meters.total_pages);
    } catch (error) {
      console.error("Error fetching meters:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getEmergencyInfo = async () => {
    if (selectedCompany.value !== "houses_info") return;
    setIsLoading(true);
    try {
      const response = await BillsService.getEmergencyInfo(
        Number(selectedBuildAddress.value)
      );
      setEmergencyInfo(response);
    } catch (error) {
      console.error("Error fetching emergency info:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchApartmentInfo = async () => {
    if (selectedCompany.value !== "apartment_info") return;
    setIsLoading(true);
    try {
      const response = await HouseManagerApi.getApartmentInfo(
        selectedBuildAddress.value,
        page,
        pageSize
      );
      setPage(response.current_page);
      setUsersInfo(response.apartments);
      setTotalPages(response.total_pages);
    } catch (error) {
      console.error("Error fetching apartment info:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (
      !isEditingCurrentPeriod &&
      selectedPeriodOptions.value !== "closed_periods"
    ) {
      fetchBills(true, false);
    } else if (
      !isEditingClosedPeriod &&
      selectedPeriodOptions.value !== "current_period"
    ) {
      fetchBills(false, false);
    }
  }, [isEditingCurrentPeriod, isEditingClosedPeriod, selectedPeriodOptions]);

  useEffect(() => {
    setPage(1);
    setTotalPages(1);
    setIsEditingCurrentPeriod(false);
    setIsEditingClosedPeriod(false);
    setIsEditingHousesInfo(false);
  }, [selectedPeriodOptions, selectedCompany]);

  useEffect(() => {
    fetchBills(selectedPeriodOptions.value === "closed_periods" ? false : true);
    fetchMeters();
    fetchApartmentInfo();
    fetchNotices();
  }, [
    selectedFilters,
    page,
    pageSize,
    selectedCompany,
    selectedBuildAddress,
    selectedPeriodOptions,
    periodMonthYear,
  ]);

  useEffect(() => {
    fetchBuildings();
  }, []);

  useEffect(() => {
    if (
      selectedCompany.value !== "houses_info" ||
      selectedBuildAddress.value == null
    )
      return;
    getEmergencyInfo();
  }, [selectedBuildAddress, isEditingHousesInfo, selectedCompany]);

  useEffect(() => {
    setPeriodMonthYear("");
    fetchBills(true, true);
    setSelectedPeriodOptions(periodOptions[1]);
  }, [selectedBuildAddress]);

  useEffect(() => {
    const handleAddressSelect = () => {
      const selectedBuilding = buildings?.buildings?.find(
        (building) =>
          building.building_id === Number(selectedBuildAddress.value)
      );
      if (selectedBuilding) {
        const formattedApartments = selectedBuilding.apartments.map((apt) => ({
          label: apt.apartment_number?.toString() || "",
          value: apt.apartment_number,
        }));
        setApartmentNumber(formattedApartments);
      } else {
        setSelectedFilters((prev) => {
          const rest = { ...prev };
          delete rest.apartment;
          delete rest.address;
          return rest;
        });

        setApartmentNumber([
          { label: t("house_manager.select_address_first"), value: undefined },
        ]);
      }
    };

    handleAddressSelect();
  }, [selectedBuildAddress]);

  useEffect(() => {
    getSettings().then((settings) => settings.PAYMENT_FEE);
    getEmergencyInfo();
  }, []);

  const companyContent = () => {
    switch (selectedCompany.value) {
      case "bills":
        return {
          header: (
            <div className="flex flex-row gap-2">
              <div className="text-[24px] font-medium">
                {t("house_manager.bills")}
              </div>

              {bills?.apartments.length !== 0 && (
                <div className="flex flex-row gap-2 items-center">
                  <span className="px-2 rounded-[20px] text-[16px] text-[#00000080] bg-[#F5F5F5]">
                    {bills?.all_invoices}
                  </span>
                  {selectedPeriodOptions.value == "closed_periods" && (
                    <>
                      <span className="text-[#FF0000] text-[12px] bg-[#FF00001A] px-2 py-[1px] rounded-[24px] font-medium">
                        {t("apartment.debt")} {bills?.count_of_debt_invoices}
                      </span>
                      <span className="text-[#0027CB] text-[12px] bg-[#0027CB1A] px-2 py-[1px] rounded-[24px] font-medium">
                        {t("apartment.unpaid")}{" "}
                        {bills?.count_of_unpaid_invoices}
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>
          ),
          extraFilter: (
            <>
              <div className="flex flex-row gap-2 items-center justify-between">
                <span className="text-[20px] font-medium min-w-[30%] flex flex-row gap-4 items-end">
                  {t("house_manager.of_period")}
                  {selectedPeriodOptions.value == "current_period" && (
                    <span className="text-[#00000080] text-[15px]">
                      {periodMonthYear &&
                        t(
                          `monthsTranslations.${periodMonthYear.split("-")[1]}`
                        )}{" "}
                      {periodMonthYear && periodMonthYear.split("-")[0]}
                    </span>
                  )}
                </span>
                <div className="flex flex-row gap-2 items-center">
                  {periodOptions.map((company) => {
                    const isDisabled =
                      company.value === "current_period" && !periodMonthYear;

                    return (
                      <div
                        key={company.value}
                        className={`
                          border-2 py-1 px-4 text-[17px] rounded-[20px] select-none font-medium transition-all
                          ${
                            company.value === selectedPeriodOptions.value
                              ? "text-blue-medium border-blue-medium"
                              : "text-[#00000080] border-[#00000080]"
                          }
                          ${
                            isDisabled
                              ? "text-[#B7B7B7] border-[#E5E5E5] bg-[#0000001A] cursor-not-allowed"
                              : "cursor-pointer"
                          }
                       `}
                        onClick={() => {
                          if (!isDisabled) {
                            setSelectedPeriodOptions(company);
                          }
                        }}
                      >
                        {company.name}
                      </div>
                    );
                  })}
                </div>
                <div className="min-w-[30%] flex flex-row gap-[24px] justify-end">
                  {periodMonthYear && (
                    <ConfirmCancelPeriod
                      selectedBuildAddress={selectedBuildAddress}
                      setPeriodMonthYear={setPeriodMonthYear}
                      setSelectedPeriodOptions={setSelectedPeriodOptions}
                      trigger={
                        <div className="flex flex-row gap-2 items-center cursor-pointer">
                          <span className="text-[20px] font-medium text-[#FF0000]">
                            {t("popUpCancelPeriod.cancel_period")}
                          </span>
                        </div>
                      }
                    />
                  )}
                  {periodMonthYear ? (
                    <ConfirmClosePeriod
                      setPeriodMonthYear={setPeriodMonthYear}
                      setSelectedPeriodOptions={setSelectedPeriodOptions}
                      selectedBuildAddress={selectedBuildAddress}
                      trigger={
                        <div
                          key="end_the_period"
                          className="flex flex-row gap-2 items-center cursor-pointer"
                        >
                          <img
                            src={CalendarChek}
                            alt="icon"
                            className="w-[24px] h-[24px]"
                          />
                          <span className="text-[20px] font-medium text-[#0027CB]">
                            {t("house_manager.end_the_period")}
                          </span>
                        </div>
                      }
                    />
                  ) : (
                    <OpenPeriod
                      setSelectedPeriodOptions={setSelectedPeriodOptions}
                      setPeriodMonthYear={setPeriodMonthYear}
                      selectedBuildAddress={selectedBuildAddress}
                      trigger={
                        <div
                          key="open_a_period"
                          className="flex flex-row gap-2 items-center cursor-pointer"
                        >
                          <img
                            src={CalendarPlus}
                            alt="icon"
                            className="w-[24px] h-[24px]"
                          />
                          <span className="text-[20px] font-medium text-[#0027CB]">
                            {t("house_manager.open_a_period")}
                          </span>
                        </div>
                      }
                    />
                  )}
                </div>
              </div>
              <div className="border-b border-[#0000001A]" />
            </>
          ),
          filerSelectors: [
            {
              label: t("house_manager.apartment"),
              value: "apartment",
              options: apartmentNumber,
            },
            selectedPeriodOptions.value === "closed_periods" && {
              translate: "apartment",
              label: t("house_manager.status"),
              value: "status",
              options: [
                { label: "all_statuses", value: null },
                { label: "debt", value: "debt" },
                { label: "unpaid", value: "unpaid" },
                { label: "paid", value: "paid" },
              ],
            },
            selectedPeriodOptions.value === "closed_periods" && {
              translate: "monthsTranslations",
              label: t("house_manager.month"),
              value: "month",
              options: [
                { label: "01", value: "1" },
                { label: "02", value: "2" },
                { label: "03", value: "3" },
                { label: "04", value: "4" },
                { label: "05", value: "5" },
                { label: "06", value: "6" },
                { label: "07", value: "7" },
                { label: "08", value: "8" },
                { label: "09", value: "9" },
                { label: "10", value: "10" },
                { label: "11", value: "11" },
                { label: "12", value: "12" },
              ],
            },
            selectedPeriodOptions.value === "closed_periods" && {
              label: t("house_manager.year"),
              value: "year",
              options: [
                { label: "2025", value: "2025" },
                { label: "2024", value: "2024" },
                { label: "2023", value: "2023" },
                { label: "2022", value: "2022" },
                { label: "2021", value: "2021" },
                { label: "2020", value: "2020" },
              ],
            },
          ].filter(Boolean) as FilterSelector[],
          content: (
            <HouseManagerBills
              data={bills?.apartments}
              selectedPeriod={selectedPeriodOptions.value}
              isEditingCurrentPeriod={isEditingCurrentPeriod}
              isEditingClosedPeriod={isEditingClosedPeriod}
              changedEntities={changedEntitiesBills}
              setChangedEntities={setChangedEntitiesBills}
              localPdfFiles={localPdfFiles}
              setLocalPdfFiles={setLocalPdfFiles}
              payBeforeDateForCurrentPeriod={payBeforeDateForCurrentPeriod}
            />
          ),
        };
      case "meters":
        return {
          header: (
            <div className="flex flex-row gap-2">
              <div className="text-[24px] font-medium">
                {t("meters.meters")}
              </div>

              {meters?.result?.length && (
                <div className="flex flex-row gap-2 items-center">
                  <span className="px-2 rounded-[20px] text-[16px] text-[#00000080] bg-[#F5F5F5]">
                    {meters?.all_apartments}
                  </span>
                  <span className="text-[#FF0000] text-[12px] bg-[#FF00001A] px-2 py-[1px] rounded-[24px] font-medium">
                    {t("house_manager.not_received")}{" "}
                    {meters?.not_received_readings_count}
                  </span>
                </div>
              )}
            </div>
          ),
          filerSelectors: [
            {
              label: t("house_manager.apartment"),
              value: "apartment",
              options: apartmentNumber,
            },
            {
              translate: "meters",
              label: t("meters.type_of_meter"),
              value: "type_of_meter",
              options: [
                {
                  label: "cold_water",
                  value: "cold_water",
                },
                {
                  label: "hot_water",
                  value: "hot_water",
                },
                {
                  label: "heating",
                  value: "heating",
                },
                {
                  label: "gas",
                  value: "gas",
                },
                {
                  label: "electricity",
                  value: "electricity",
                },
              ],
            },
            {
              translate: "apartment",
              label: t("house_manager.status"),
              value: "status",
              options: [
                { label: "all_statuses", value: null },
                { label: "not_received", value: "notreceived" },
              ],
            },
          ] as FilterSelector[],
          content: <HouseManagerMeters data={meters} />,
        };
      case "notices_and_news":
        return {
          header: (
            <>
              <span className="text-[24px] font-medium">
                {t("house_manager.notices_and_news")}
              </span>
              <PopUpCreateNews
                building_id={Number(selectedBuildAddress.value)}
                fetchNotices={fetchNotices}
                trigger={
                  <div className="flex flex-row gap-2 bg-[#0064FF] px-[10px] py-[5px] rounded-[8px] items-center cursor-pointer">
                    <Plus className="text-white" width={24} height={24} />
                    <span className="text-[20px] font-medium text-white">
                      {t("popUpCreateNews.create_news")}
                    </span>
                  </div>
                }
              />
            </>
          ),
          content: (
            <HouseManagerNoticesAndNews
              building_id={selectedBuildAddress.value}
              fetchNotices={fetchNotices}
              notices={notices}
            />
          ),
        };
      case "apartment_info":
        return {
          header: (
            <>
              <span className="text-[24px] font-medium">
                {t("house_manager.apartment_info")}
              </span>
            </>
          ),
          // filerSelectors: [
          //   {
          //     label: t("popUpAddBill.apartment"),
          //     value: "apartment",
          //     options: apartmentNumber,
          //   },
          // ] as FilterSelector[],
          content: <HouseManagerApartamentInfo data={usersInfo} />,
        };
      case "houses_info":
        return {
          header: (
            <>
              <span className="text-[24px] font-medium">
                {t("house_manager.houses_info")}
              </span>
              <div
                key={isEditingHousesInfo ? "finish_editing" : "edit_data"}
                onClick={async () => {
                  if (isEditingHousesInfo) {
                    const result = await handleSave();
                    if (result) {
                      setIsEditingHousesInfo(false);
                    }
                  } else {
                    setIsEditingHousesInfo(true);
                  }
                }}
                className="flex flex-row gap-2 bg-[#0064FF] px-[10px] py-[5px] rounded-[8px] items-center cursor-pointer"
              >
                <Edit className="text-white" width={24} height={24} />
                <span className="text-[20px] font-medium text-white">
                  {isEditingHousesInfo
                    ? t("house_manager.finish_editing")
                    : t("house_manager.edit_data")}
                </span>
              </div>
            </>
          ),
          content: (
            <HouseManagerHousesInfo
              selectedBuildAddress={selectedBuildAddress}
              isEditingHouseInfo={isEditingHousesInfo}
              data={emergencyInfo}
              onSaveRef={refSendHouseInfo}
              loadHouseInfo={isLoading}
              getEmergencyInfo={getEmergencyInfo}
            />
          ),
        };
      default:
        return null;
    }
  };

  return (
    <div className="h-[100%] flex flex-row p-4 gap-6">
      <TabBar />
      <div className="w-full flex flex-col gap-[20px]">
        <div className="flex row items-center justify-between ml-4">
          <p id="title_bills" className="font-medium text-[20px]">
            {t("sideBar.house_manager")}
          </p>
          <HeaderCommon />
        </div>

        <div className="flex flex-col gap-2 ml-4 mb-10">
          <div className="flex flex-row gap-2 items-center justify-between">
            <div className="flex flex-row gap-2 items-center w-[15%]">
              <div className="text-[24px] font-medium">
                {t("sideBar.my_houses")}
              </div>
              <span className="px-2 rounded-[20px] text-[16px] text-[#00000080] bg-[#F5F5F5]">
                {buildAddress?.length}
              </span>
            </div>
            <DashboardTabs
              data={buildAddress}
              selectedItem={selectedBuildAddress}
              setSelectedItem={setSelectedBuildAddress}
            />
            <div className="w-[15%]" />
          </div>
          <div className="border-b border-[#0000001A]" />

          <div className="flex flex-row gap-2 justify-between items-center">
            <span className="text-[20px] font-medium w-[20%]">
              {t("house_manager.section")}
            </span>
            <div className="flex flex-row gap-2 items-center">
              <DashboardTabs
                data={companyOptions}
                selectedItem={selectedCompany}
                setSelectedItem={setSelectedCompany}
              />
            </div>
            <span className="w-[20%]" />
          </div>

          <div className="border-b border-[#0000001A]" />

          {companyContent()?.extraFilter}

          <div className="flex flex-row gap-2 items-center justify-between">
            {companyContent()?.header}

            {companyContent()?.filerSelectors && (
              <div className="flex flex-row gap-[24px] items-center">
                {selectedPeriodOptions.value === "current_period" &&
                  isEditingCurrentPeriod && (
                    <div className="w-[180px]">
                      <CalendarForm
                        placeholder={t("house_manager.pick_date")}
                        setDueDate={(value) => {
                          setPayBeforeDateForCurrentPeriod(value);
                        }}
                        prevDueDate={payBeforeDateForCurrentPeriod ?? ""}
                      />
                    </div>
                  )}
                {companyContent()?.filerSelectors?.map((item, index) => {
                  return (
                    <SelectTab
                      key={index}
                      translate={item?.translate}
                      selectedTab={selectedFilters[item.value] || item.value}
                      setSelectedTab={(newValue: string) => {
                        setSelectedFilters((prev) => ({
                          ...prev,
                          [item.value]: newValue,
                        }));
                      }}
                      tabs={item.options}
                      titleLabel={item.label}
                    />
                  );
                })}
                <div
                  key="clean_filter"
                  className={`${
                    Object.keys(selectedFilters).length > 0
                      ? "text-[#0027CB] cursor-pointer"
                      : "text-[#00000033] cursor-not-allowed"
                  } text-[17px] font-medium flex flex-row items-center`}
                  onClick={() => {
                    if (Object.keys(selectedFilters).length > 0) {
                      setSelectedFilters({});
                    }
                  }}
                >
                  {t("house_manager.clean_filter")}{" "}
                  <CrossRight className="w-[24px] h-[24px]" />
                </div>
                {selectedCompany.value === "bills" && (
                  <>
                    <div
                      onClick={() =>
                        selectedPeriodOptions.value === "closed_periods"
                          ? setIsEditingClosedPeriod(!isEditingClosedPeriod)
                          : editCurrentPeriod()
                      }
                      key={
                        isEditingCurrentPeriod || isEditingClosedPeriod
                          ? "finish_editing"
                          : "edit_bills"
                      }
                      className="flex flex-row gap-2 bg-[#0064FF] px-[10px] py-[5px] rounded-[8px] items-center cursor-pointer"
                    >
                      <Edit className="text-white" width={24} height={24} />
                      <span className="text-[20px] font-medium text-white">
                        {isEditingCurrentPeriod || isEditingClosedPeriod
                          ? t("house_manager.finish_editing")
                          : t("house_manager.edit_bills")}
                      </span>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          {!isLoading && !isLoadingBills ? (
            companyContent()?.content || <div>No content available</div>
          ) : (
            <div className="mx-auto">
              <Loader isGrey />
            </div>
          )}
          {totalPages >= 2 && (
            <Pagination
              page={page}
              pageSize={pageSize}
              totalPages={totalPages}
              setPage={setPage}
              setPageSize={setPageSize}
              contentLength={10}
              storageKey="house-manager-page-size"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default HouseManager;
